import React, { useState, useEffect, createContext, useContext } from 'react';

// Session context for sharing user session data
const SessionContext = createContext();

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

export const SessionProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkSession();
  }, []);

  const checkSession = async () => {
    try {
      setLoading(true);
      
      // Get user data from URL parameters (passed from Core)
      const urlParams = new URLSearchParams(window.location.search);
      const userParam = urlParams.get('user');
      
      if (userParam) {
        const userData = JSON.parse(decodeURIComponent(userParam));
        setUser(userData);
        setError(null);
        console.log('✅ User data loaded from URL:', userData);
      } else {
        // Fallback: try to get from Core API
        const response = await fetch('https://az-core.precisioncompliancegroup.com/api/auth/session', {
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error('Session check failed');
        }

        const data = await response.json();
        
        if (data.success && data.user) {
          setUser(data.user);
          setError(null);
        } else {
          setUser(null);
          setError('Not authenticated');
        }
      }
    } catch (err) {
      console.error('Session check error:', err);
      setUser(null);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    loading,
    error,
    checkSession
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
};

// Access control component
export const AccessControl = ({ children, requiredProtocol }) => {
  const { user, loading } = useSession();

  if (loading) {
    return <div className="flex items-center justify-center p-8">Loading...</div>;
  }

  if (!user || !user.app_roles || !user.app_roles.scrubber) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">No Access to Scrubber</h2>
          <p className="text-gray-600">Contact your administrator to request access.</p>
        </div>
      </div>
    );
  }

  if (requiredProtocol && (!user.app_roles.scrubber.scopes || !user.app_roles.scrubber.scopes.includes(requiredProtocol))) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Protocol Access Denied</h2>
          <p className="text-gray-600">You don't have access to the {requiredProtocol} protocol.</p>
        </div>
      </div>
    );
  }

  return children;
};

// User info display component - removed since Core handles authentication
export const UserInfo = () => {
  // Return null - no user info display in Scrubber
  return null;
};
